import React, { useState, useMemo } from 'react';
import { Mafs, Coordinates, Circle, Line, Point, Text, Polygon, Theme } from 'mafs';

// Helper to draw a right angle marker
const RightAngleMarker = ({ vertex, p1, p2, size = 0.6, color = Theme.blue }) => {
    const angle1 = Math.atan2(p1[1] - vertex[1], p1[0] - vertex[0]);
    const angle2 = Math.atan2(p2[1] - vertex[1], p2[0] - vertex[0]);

    const mid1 = [vertex[0] + Math.cos(angle1) * size, vertex[1] + Math.sin(angle1) * size];
    const mid2 = [vertex[0] + Math.cos(angle2) * size, vertex[1] + Math.sin(angle2) * size];
    const corner = [mid1[0] + mid2[0] - vertex[0], mid1[1] + mid2[1] - vertex[1]];

    return (
        <Line.Polyline
            points={[mid1, corner, mid2]}
            color={color}
            weight={2}
        />
    );
};

export const CircleTheorems = ({ config }) => {
    const [theorem, setTheorem] = useState(config.theorem || 'angleInSemicircle');
    const center = config.center || { x: 0, y: 0 };
    const radius = config.radius || 5;

    // Interactive points
    const [t, setT] = useState(0.8); // Parametric angle for dynamic points

    const getPoint = (angle) => [
        center.x + radius * Math.cos(angle),
        center.y + radius * Math.sin(angle)
    ];

    const circleEquation = useMemo(() => {
        const h = center.x === 0 ? 'x²' : `(x ${center.x > 0 ? '-' : '+'} ${Math.abs(center.x)})²`;
        const k = center.y === 0 ? 'y²' : `(y ${center.y > 0 ? '-' : '+'} ${Math.abs(center.y)})²`;
        return `${h} + ${k} = ${radius * radius}`;
    }, [center, radius]);

    const renderAngleInSemicircle = () => {
        const A = getPoint(Math.PI);
        const B = getPoint(0);
        const C = getPoint(t);
        return (
            <>
                <Line.Segment point1={A} point2={B} color={Theme.indigo} weight={2} />
                <Polygon points={[A, B, C]} color={Theme.blue} fillOpacity={0.1} />
                <RightAngleMarker vertex={C} p1={A} p2={B} color={Theme.red} />

                <Point x={A[0]} y={A[1]} color={Theme.indigo} />
                <Point x={B[0]} y={B[1]} color={Theme.indigo} />
                <Point x={C[0]} y={C[1]} color={Theme.red} />
                <Text x={C[0]} y={C[1] + 0.5}>90°</Text>
            </>
        );
    };

    const renderTangentRadius = () => {
        const tangentAngle = t;
        const T = getPoint(tangentAngle);

        // Tangent line endpoints
        const p1 = [T[0] + 4 * Math.cos(tangentAngle + Math.PI/2), T[1] + 4 * Math.sin(tangentAngle + Math.PI/2)];
        const p2 = [T[0] - 4 * Math.cos(tangentAngle + Math.PI/2), T[1] - 4 * Math.sin(tangentAngle + Math.PI/2)];

        return (
            <>
                <Line.Segment point1={[center.x, center.y]} point2={T} color={Theme.blue} />
                <Line.Segment point1={p1} point2={p2} color={Theme.green} weight={3} />
                <RightAngleMarker vertex={T} p1={[center.x, center.y]} p2={p1} color={Theme.red} />

                <Point x={T[0]} y={T[1]} color={Theme.red} />
                <Text x={T[0] + 0.5} y={T[1] + 0.5}>Point of Tangency</Text>
            </>
        );
    };

    const renderChordChord = () => {
        const A = getPoint(t);
        const B = getPoint(t + 2.5);
        const C = getPoint(t + 1);
        const D = getPoint(t - 1.5);

        return (
            <>
                <Line.Segment point1={A} point2={B} color={Theme.indigo} />
                <Line.Segment point1={C} point2={D} color={Theme.orange} />
                <Point x={A[0]} y={A[1]} color={Theme.indigo} />
                <Point x={B[0]} y={B[1]} color={Theme.indigo} />
                <Point x={C[0]} y={C[1]} color={Theme.orange} />
                <Point x={D[0]} y={D[1]} color={Theme.orange} />
            </>
        );
    };

    const renderPowerOfPoint = () => {
        const P = [center.x + 8, center.y]; // External point
        const angle1 = t / 3;
        const angle2 = -t / 4;

        // Simplified intersection approximations for demonstration
        const A = getPoint(angle1);
        const C = getPoint(angle2);
        const B = getPoint(Math.PI - angle1 * 0.5);
        const D = getPoint(Math.PI - angle2 * 0.5);

        return (
            <>
                <Line.Segment point1={P} point2={B} color={Theme.blue} />
                <Line.Segment point1={P} point2={D} color={Theme.green} />

                <Point x={P[0]} y={P[1]} color={Theme.red} />
                <Text x={P[0] + 0.5} y={P[1]}>P</Text>

                <Point x={A[0]} y={A[1]} color={Theme.blue} />
                <Point x={B[0]} y={B[1]} color={Theme.blue} />
                <Point x={C[0]} y={C[1]} color={Theme.green} />
                <Point x={D[0]} y={D[1]} color={Theme.green} />
            </>
        );
    };

    return (
        <div className="ch-circle-theorems">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                {[
                    { id: 'angleInSemicircle', label: 'Angle in Semicircle' },
                    { id: 'tangentRadius', label: 'Tangent-Radius' },
                    { id: 'chordChord', label: 'Intersecting Chords' },
                    { id: 'powerOfPoint', label: 'Power of a Point' },
                    { id: 'circleEquation', label: 'Circle Equation' }
                ].map(th => (
                    <button
                        key={th.id}
                        onClick={() => setTheorem(th.id)}
                        style={{ padding: '8px 16px', background: theorem === th.id ? '#1890ff' : '#f0f0f0', color: theorem === th.id ? '#fff' : '#000', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        {th.label}
                    </button>
                ))}
            </div>

            <div style={{ marginBottom: '20px', padding: '15px', background: '#e6f7ff', borderRadius: '8px' }}>
                {theorem === 'angleInSemicircle' && <p><strong>Angle in a Semicircle:</strong> The angle subtended by a diameter at the circumference is always 90°.</p>}
                {theorem === 'tangentRadius' && <p><strong>Tangent-Radius Theorem:</strong> A tangent is perpendicular to the radius at the point of contact.</p>}
                {theorem === 'chordChord' && <p><strong>Intersecting Chords:</strong> When two chords intersect inside a circle, the product of their segments is equal.</p>}
                {theorem === 'powerOfPoint' && <p><strong>Power of a Point:</strong> For an external point P and secants PAB and PCD, PA × PB = PC × PD.</p>}
                {theorem === 'circleEquation' && <p><strong>Circle Equation:</strong> {circleEquation}</p>}
            </div>

            <Mafs viewBox={{ x: [-radius-4, radius+4], y: [-radius-2, radius+2] }} height={400} pan={false}>
                {theorem === 'circleEquation' && <Coordinates.Cartesian />}

                <Circle center={[center.x, center.y]} radius={radius} color={Theme.blue} strokeWidth={2} />
                <Point x={center.x} y={center.y} color={Theme.blue} />
                <Text x={center.x} y={center.y - 0.5}>O</Text>

                {theorem === 'angleInSemicircle' && renderAngleInSemicircle()}
                {theorem === 'tangentRadius' && renderTangentRadius()}
                {theorem === 'chordChord' && renderChordChord()}
                {theorem === 'powerOfPoint' && renderPowerOfPoint()}
            </Mafs>

            <div style={{ marginTop: '15px' }}>
                <label>Drag to explore: </label>
                <input type="range" min="0" max={Math.PI * 2} step="0.1" value={t} onChange={(e) => setT(parseFloat(e.target.value))} style={{ width: '100%' }} />
            </div>
        </div>
    );
};